<template>
  <div>
    <apply-template-sidenav @template-applied="onTemplateApplied" :is-sidebar-active.sync="isApplyTemplateSidebarActive"
      :panel-data="selectedPanel" :template-options="templateOptions" :uuid="selectedPanel && selectedPanel.uuid"
      @refetch-data="refetchData" />
    <toastification-loading-content ref="loadingToast" />
    <toastification-progress-content ref="progressToast" />

    <dmp-panels-wizard :is-active.sync="isWizardActive" :item-edit.sync="itemEdit" :is-add.sync="isAdd"
      :facilities-options="facilitiesOptions" :center-options="centerOptions" :sector-options="sectorOptions"
      :reseller-options="resellerOptions" @close-wizard="closeWizard" @refetch-data="refetchData" />
    <b-row v-if="showFilters" class="pt-2 pb-1">
      <b-col cols="6" class="d-flex align-items-center">
        <BreadcrumbMenu />
      </b-col>
      <b-col cols="6" class="pr-3 text-right">
        <button-filters @show-modal="$root.$emit('bv::show::modal', $event)" :filters-count="filtersCount"
          @clear-search-filters="clearSearchFilters" />
      </b-col>
    </b-row>

    <filter-modal @action="buildFilters()" @clear="clearSearchFilters()">
      <template v-slot:main-filters>
        <filters :facilities="facilitiesOptions"
          :facilities-filter.sync="facilitiesFilter"
          :centers="centerOptions"
          :center-filter.sync="centerFilter"
          :sectors="sectorOptions"
          :sector-filter.sync="sectorFilter"
          :profile-options="profileOptions"
          :profile-filter.sync="profileFilter"
          :custom-query-params.sync="customQueryParams"
          :municipality-options="municipalityOptions"
          :region-options="regionOptions"
        />
      </template>
    </filter-modal>

    <!-- Table Container Card -->
    <b-card-group deck :class="{ 'mx-2': showFilters, 'm-0': !showFilters }">
      <b-card class="m-0" no-body>
        <div class="m-2">
          <!-- Table Top -->
          <b-row>
            <!-- Per Page -->
            <b-col cols="12" md="4" class="d-flex align-items-center justify-content-start mb-1 mb-md-0">
              <label class="text-capitalize">{{ $t("table.show") }}</label>
              <v-select v-model="perPage" :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'" :options="perPageOptions"
                :clearable="false" class="per-page-selector d-inline-block mx-50" />
              <label class="text-capitalize">{{ $t("table.entries") }}</label>
            </b-col>

            <!-- Search -->
            <b-col cols="12" md="8">
              <div class="device-list-actions d-flex align-items-center justify-content-end">
                <FacetedSearch :options="searchOptions" default-field="name" class="device-list-search mr-1"
                  @search="setFacetedFilters" />
                <b-button variant="primary" @click="openAdd()" v-if="$can('create', 'Dmp') && showFilters">
                  <span class="text-nowrap text-capitalize">{{
                    `${$t("actions.add")} ${$tc("dmp.panel", 1)}`
                    }}</span>
                </b-button>
                <b-button v-if="showFilters" variant="success" class="text-capitalize ml-1" type="button"
                  :disabled="buttonExport" :title="$t('messages.export.title')" @click="exportExcel">
                  <feather-icon icon="DownloadCloudIcon" size="15" />
                </b-button>
                <button class="btn btn-success ml-1" @click="refetchData()">
                  <feather-icon icon="RefreshCwIcon" size="14" />
                </button>
              </div>
            </b-col>
          </b-row>
        </div>

        <b-table ref="refListTable" class="position-relative" :items="fetchList" responsive :fields="tableColumns"
          primary-key="id" :sort-by.sync="sortBy" :sort-desc.sync="isSortDirDesc" show-empty
          :empty-text="$t('no_record_found')">
          <template #head()="data">
            <span> {{ $tc(data.label, 1) }}</span>
          </template>

          <template #cell(router_status)="data">
            <div :style="`background: ${data.item.device.units.active ? 'green' : 'red'};
                width: 1.5rem;
                height: 1.5rem;
                border-radius: 1rem;
                text-align: center;`" :title="$t(`dmp.${data.item.device.units.active ? 'connected' : 'disconnected'}`)">
            </div>
          </template>

          <template #cell(panel_status)="data">
            <div :style="`background: ${data.item.device.online ? 'green' : 'red'};
                width: 1.5rem;
                height: 1.5rem;
                border-radius: 1rem;
                text-align: center;`" :title="$t(`dmp.${data.item.device.online ? 'connected' : 'disconnected'}`)">
            </div>
          </template>

          <template #cell(arm_status)="data">
            <div :style="`background: ${data.item.active ? 'green' : 'orange'};
                width: 1.5rem;
                height: 1.5rem;
                border-radius: 1rem;
                text-align: center;`" :title="$t(`dmp.${data.item.active ? 'panelStatusArmed' : 'panelStatusDisarmed'}`)">
            </div>
          </template>

          <template #cell(arming_action)="data">
            <div class="text-start">
              <button v-if="!data.item.active && $can('arm_panel', 'DmpPanel')" class="btn btn-sm btn-primary"
                :disabled="loading" @click="armPanel(data.item)">
                {{ $t("dmp.armPanel") }}
              </button>
              <button v-if="data.item.active && $can('disarm_panel', 'DmpPanel')" class="btn btn-sm btn-primary"
                :disabled="loading" @click="armPanel(data.item)">
                {{ $t("dmp.disarmPanel") }}
              </button>
            </div>
          </template>

          <template #head(name)="data">
            <span> {{ $tc(data.label, 2) }}</span>
          </template>

          <template #head(panel_host)="data">
            <span> {{ $tc(data.label, 2) }}</span>
          </template>

          <template #cell(unit.description)="data">
            {{ unitsSite(data.item) }}
          </template>

          <template #head(center.name)="data">
            <span> {{ $tc(data.label, 2) }}</span>
          </template>

          <template #head(sector.name)="data">
            <span> {{ $tc(data.label, 2) }}</span>
          </template>

          <template #cell(uuid)="data">
            <div v-clipboard:copy="`${data.item.uuid}`" v-clipboard:success="onCopy" v-clipboard:error="onError"
              style="cursor: pointer">
              <feather-icon icon="CopyIcon" size="16" class="align-middle text-body" />
            </div>
          </template>

          <template #cell(panel_account)="data">
            <div style="cursor: pointer" @click="copyToClipboard(data.item.id, 'panel_account')">
              <feather-icon icon="CopyIcon" size="16" class="align-middle text-body" />
            </div>
          </template>

          <template #cell(panel_remote_key)="data">
            <div style="cursor: pointer" @click="copyToClipboard(data.item.id, 'panel_remote_key')">
              <feather-icon icon="CopyIcon" size="16" class="align-middle text-body" />
            </div>
          </template>

          <template #cell(enabled_zones_audit)="data">
            <div class="text-center">
              <feather-icon :icon="data.value ? 'CheckCircleIcon' : 'XCircleIcon'"
                :class="data.value ? 'text-success' : 'text-danger'" size="18" />
            </div>
          </template>

          <template #cell(enabled_areas_audit)="data">
            <div class="text-center">
              <feather-icon :icon="data.value ? 'CheckCircleIcon' : 'XCircleIcon'"
                :class="data.value ? 'text-success' : 'text-danger'" size="18" />
            </div>
          </template>

          <!-- Column: Actions -->
          <template #cell(actions)="data">
            <div v-if="data.item.status">
              <b-button :id="`actions-disabled-${data.item.id}`" tabindex="0" variant="white" class="px-1 btn-y"
                disabled>
                <feather-icon icon="ClockIcon" size="16" class="align-middle text-black" />
              </b-button>
              <b-tooltip :target="`actions-disabled-${data.item.id}`" placement="top">
                {{ $t("dmp.panelBusy") }}
              </b-tooltip>
            </div>
            <div v-else>
              <b-button :id="`actions-${data.item.id}`" tabindex="0" variant="white" class="px-1 btn-y">
                <feather-icon icon="MoreVerticalIcon" size="16" class="align-middle text-body" />
              </b-button>
              <b-popover :target="`actions-${data.item.id}`" triggers="focus" placement="bottom"
                custom-class="popover-p-0" variant="white">
                <b-list-group class="m-0">
                  <b-list-group-item v-if="$can('generate_template', 'Dmp')" class="border-0" button
                    @click="generateTemplate(data.item)">
                    <feather-icon icon="UploadCloudIcon" />
                    <span class="align-middle ml-50 text-capitalize">
                      {{ $t("dmp.templateConfig.generateTemplate") }}
                    </span>
                  </b-list-group-item>

                  <b-list-group-item v-if="$can('apply_template', 'Dmp')" class="border-0" button
                    @click="confirmApplyTemplate(data.item)">
                    <feather-icon icon="DownloadCloudIcon" />
                    <span class="align-middle ml-50 text-capitalize">
                      {{ $t("dmp.templateConfig.applyTemplate") }}
                    </span>
                  </b-list-group-item>

                  <b-list-group-item v-if="$can('view_devices', 'Dmp')" class="border-0"
                    :to="`/dmp/panels/${data.item.id}/devices`">
                    <feather-icon icon="HardDriveIcon" />
                    <span class="align-middle ml-50 text-capitalize">
                      {{ $t("dmp.devicesSection") }}
                    </span>
                  </b-list-group-item>

                  <b-list-group-item v-if="$can('update', 'Dmp')" class="border-0" button @click="openEdit(data.item)">
                    <feather-icon icon="EditIcon" />
                    <span class="align-middle ml-50 text-capitalize">
                      {{ $t("actions.edit") }}
                    </span>
                  </b-list-group-item>

                  <b-list-group-item v-if="$can('delete', 'Dmp')" class="border-0" button
                    @click="confirmDelete(data.item.id)">
                    <feather-icon icon="TrashIcon" />
                    <span class="align-middle ml-50 text-capitalize">
                      {{ $t("actions.delete") }}
                    </span>
                  </b-list-group-item>

                  <b-list-group-item v-if="$can('view_config', 'Dmp')" class="border-0"
                    :to="`/dmp/panels/${data.item.id}/config`">
                    <feather-icon icon="SettingsIcon" />
                    <span class="align-middle ml-50 text-capitalize">
                      {{ $t("dmp.config") }}
                    </span>
                  </b-list-group-item>

                </b-list-group>
              </b-popover>
            </div>
          </template>
        </b-table>
        <!-- Footer Table -->
        <div class="mx-2 mb-2">
          <b-row>
            <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-start">
              <span class="text-muted text-capitalize">{{
                $t("table.showing", {
                  from: dataMeta.from,
                  to: dataMeta.to,
                  of: dataMeta.of,
                })
              }}</span>
            </b-col>
            <!-- Pagination -->
            <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-end">
              <Pagination :totalList="totalList" :currentPage="currentPage" @updatePagination="update"
                :perPage="perPage">
              </Pagination>
            </b-col>
          </b-row>
        </div>
      </b-card>
    </b-card-group>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BTable,
  BButton,
  BDropdown,
  BDropdownItem,
  BPagination,
  BInputGroup,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { ref, getCurrentInstance, watch } from "@vue/composition-api";
import Swal from "sweetalert2";
import useList from "./useList";
import Sidenav from "./Sidenav.vue";
import { useToast } from "vue-toastification/composition";
import ToastificationContent
  from "@core/components/toastification/ToastificationContent.vue";
import ToastificationLoadingContent
  from "@core/components/toastification/ToastificationLoadingContent.vue";
import ToastificationProgressContent
  from "@core/components/toastification/ToastificationProgressContent.vue";
import store from "@/store";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import Pagination from "@/components/Pagination.vue";
import BreadcrumbMenu from "@/components/BreadcrumbMenu.vue";
import FilterModal from "@/components/Filters/FilterModal.vue";
import ButtonFilters from "@/components/Filters/ButtonFilters.vue";
import Filters from "./Filters.vue";
import DmpPanelsWizard from "./dmp_panels_wizard/Index.vue";
import ApplyTemplateSidenav from "./SidenavTemplate.vue";
import FacetedSearch from "@/components/FacetedSearch.vue";


export default {
  components: {
    ApplyTemplateSidenav,
    Sidenav,
    Pagination,
    DmpPanelsWizard,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BDropdown,
    BDropdownItem,
    BPagination,
    BInputGroup,
    vSelect,
    ToastificationLoadingContent,
    ToastificationProgressContent,
    BreadcrumbMenu,
    FilterModal,
    ButtonFilters,
    Filters,
    Notification,
    FacetedSearch,
  },
  props: {
    showFilters: {
      type: Boolean,
      default: true
    }
  },
  setup(_, context) {
    const { proxy } = getCurrentInstance();

    //start pagination
    const update = (data) => {
      currentPage.value = data;
    };
    //end pagination

    const loadingToast = ref(null);
    const isWizardActive = ref(false);
    const itemEdit = ref({});
    const isAdd = ref(false);
    const loading = ref(false);
    const isApplyTemplateSidebarActive = ref(false);
    const selectedPanel = ref({});
    const templateOptions = ref([]);
    const activeProcesses = ref([])
    const busyPanels = ref([])
    const buttonExport = ref(false);

    const exportExcel = async () => {
      action.value = "export";
      buttonExport.value = true;
      try {
        await fetchList();
      } finally {
        buttonExport.value = false;
      }
    };

    const openEdit = (item) => {
      isWizardActive.value = true;
      itemEdit.value = JSON.parse(JSON.stringify(item));
      isAdd.value = false;
    };
    const { t } = useI18nUtils();
    const searchOptions = [
      { label: t("dmp.name"), value: "name", isArray: false },
      { label: t("dmp.units"), value: "description", isArray: false },
      { label: t("unit.rems"), value: "rems", isArray: false },
      { label: t("unit.sirh"), value: "sirh", isArray: false },
      { label: t("dmp.panel_model"), value: "panel_model", isArray: false },
      { label: t("dmp.panelHost"), value: "panel_host", isArray: false },
      { label: t("dmp.panelAccount"), value: "panel_account", isArray: false },
      { label: t("dmp.panelRemoteKey"), value: "panel_remote_key", isArray: false },
      { label: t("dmp.uuid"), value: "uuid", isArray: false },
      { label: t("dmp.instance_host"), value: "instance_host", isArray: false },
      { label: t("dmp.panel_serial_num"), value: "panel_serial_num", isArray: false },
      { label: t("dmp.panel_mac_addr"), value: "panel_mac_addr", isArray: false },
      { label: t("router.ip_vpn"), value: "router_ip", isArray: false },
      { label: t("dmp.status"), value: "status", isArray: false },
      { label: t("dmp.days_zones_audit"), value: "days_zones_audit", isArray: false },
      { label: t("dmp.days_areas_audit"), value: "days_areas_audit", isArray: false },
    ];

    const toast = useToast();

    const trans = {
      "messages.delete.title": t("messages.delete.title"),
      "messages.delete.body": t("messages.delete.body"),
      "actions.delete": t("actions.delete"),
      "actions.cancel": t("actions.cancel"),
      "dmp.actions": t("dmp.actions"),
      "dmp.syncCorrectly": t("dmp.syncCorrectly"),
      "dmp.events": t("dmp.events"),
      "dmp.status": t("dmp.status"),
      "dmp.devices": t("dmp.devices"),
      "dmp.syncPanel": t("dmp.syncPanel"),
      "dmp.syncing": t("dmp.syncing"),
      "dmp.withoutProfile": t("dmp.withoutProfile"),
      "dmp.armPanel": t("dmp.armPanel"),
      "dmp.disarmPanel": t("dmp.disarmPanel"),
      "dmp.arming": t("dmp.arming"),
      "dmp.disarming": t("dmp.disarming"),
      "dmp.armedCorrectly": t("dmp.armedCorrectly"),
      "dmp.disarmedCorrectly": t("dmp.disarmedCorrectly"),
      "dmp.panel": t("dmp.panel"),
      "messages.copy.copied_successfully": t("messages.copy.copied_successfully"),
      "messages.copy.could_not_copy": t("messages.copy.could_not_copy"),
    };

    watch(
      () => {
        const query = context.root.$route && context.root.$route.query;
        if (!query) return null;
        return query.wizardData ?? null;
      },
      async (wizardData) => {
        if (!wizardData) return;

        try {
          const data = JSON.parse(decodeURIComponent(wizardData));
          itemEdit.value.id_unit = [data.id]
          itemEdit.value.id_center = data.id_center
          itemEdit.value.id_sector = data.id_sector

          const isEditItem = data.isEdit || false;
          if (isEditItem) {
            openEdit({ ...itemEdit.value, ...data })
          } else {
            isWizardActive.value = true;
            isAdd.value = true;
          }
        } catch (error) {
          toast({
            component: ToastificationContent,
            props: {
              title:
                error.response?.data?.message ||
                t("messages.error.server_error"),
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        } finally {
          const query = { ...(context.root.$route.query || {}) };
          delete query.wizardData;

          context.root.$router.replace({ query }).catch(() => { });
        }
      },
      { immediate: true }
    );

    const {
      // methods
      fetchList,
      refetchData,
      clearSearchFilters,
      buildFilters,

      //table pagination
      tableColumns,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      perPageOptions,

      // filters
      centerFilter,
      sectorFilter,
      facilitiesFilter,
      profileFilter,

      // Options
      facilitiesOptions,
      centerOptions,
      sectorOptions,
      resellerOptions,
      profileOptions,
      municipalityOptions,
      regionOptions,

      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      formatDateAssigned,
      filtersCount,
      action,
      setFacetedFilters,
      customQueryParams,
    } = useList();

    const closeWizard = () => {
      isWizardActive.value = false;
      itemEdit.value = {};
      isAdd.value = false;
    };

    const confirmDelete = function (id) {
      const self = this;
      const storeInner = store;
      this.$swal({
        title: trans["messages.delete.title"],
        text: trans["messages.delete.body"],
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: trans["actions.delete"],
        cancelButtonText: trans["actions.cancel"],
        customClass: {
          confirmButton: "btn btn-primary text-capitalize",
          cancelButton: "btn btn-outline-danger ml-1 text-capitalize",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          storeInner
            .dispatch("dmp_panels/delete", id)
            .then((response) => {
              if (response.success) {
                toast({
                  component: ToastificationContent,
                  props: {
                    title: response.message,
                    icon: "CheckIcon",
                    variant: "success",
                  },
                });
                self.refetchData();
              } else {
                toast(
                  {
                    component: ToastificationContent,
                    props: {
                      title: response.message,
                      icon: "AlertTriangleIcon",
                      variant: "danger",
                    },
                  },
                  {
                    timeout: 10000,
                  }
                );
              }
            })
            .catch((response) => {
              toast(
                {
                  component: ToastificationContent,
                  props: {
                    title: response.response.data.message,
                    icon: "AlertTriangleIcon",
                    variant: "danger",
                  },
                },
                {
                  timeout: 10000,
                }
              );
            });
        }
      });
    };

    const openAdd = () => {
      isAdd.value = true;
      isWizardActive.value = true;
    };

    const copyToClipboard = (id, field) => {
      store
        .dispatch("dmp_panels/copyPassword", { id, field })
        .then((response) => {
          navigator.clipboard.writeText(response.data.value);
          toast({
            component: ToastificationContent,
            props: {
              title: "Copiado correctamente",
              icon: "ClipboardIcon",
              variant: "success",
            },
          });
        })
        .catch((error) => {
          toast({
            component: ToastificationContent,
            props: {
              title: "No se pudo copiar",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    };

    const onError = () => {
      toast({
        component: ToastificationContent,
        props: {
          title: trans["messages.copy.could_not_copy"],
          icon: "AlertTriangleIcon",
          variant: "danger",
        },
      });
    };

    const itemFilter = ref();

    const addItemFilter = (data) => {
      itemFilter.value = data;
    };

    const addItemFilterSearch = (data) => {
      data.forEach((element) => {
        if (nameNav.value === element.text) {
          searchQuery.value = element.searchQuery;
        }
      });
    };

    const unitsSite = (item) => {
      let { units } = item.device;

      if (units.length) {
        let unitsDescription = units.map((unit) => unit.description);

        return unitsDescription.join(",");
      }

      return "";
    };

    const nameNav = ref("breadcrumbs.iot.dmp.panels");

    const armPanel = function (panel) {
      const messageAlert = panel.active
        ? `${trans["dmp.disarmPanel"]} ${panel.name}?`
        : `${trans["dmp.armPanel"]} ${panel.name}?`;

      const confirmButton = panel.active
        ? trans["dmp.disarmPanel"]
        : trans["dmp.armPanel"];

      const obj = {
        title: messageAlert,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: confirmButton,
        cancelButtonText: trans["actions.cancel"],
        customClass: {
          confirmButton: "btn btn-primary text-capitalize",
          cancelButton: "btn btn-outline-danger ml-1 text-capitalize",
        },
        buttonsStyling: false,
      }

      if (!panel.active) {
        obj.html = `
          <div class="d-flex flex-column align-items-start px-1 w-100">
            <label class="mb-50 font-weight-bold">${trans["dmp.badZones"] || "Bad Zones"}</label>
            <select id="armModeSelect" class="form-control mb-5">
              <option value='refuse'>${trans["dmp.refuse"] || "Refuse"}</option>
              <option value='bypass'>${trans["dmp.bypass"] || "Bypass"}</option>
              <option value='force'>${trans["dmp.force"] || "Force"}</option>
            </select>
          </div>
        `;
        obj.preConfirm = () => {
          const selected = document.getElementById('armModeSelect');
          return selected ? selected.value : null;
        };
      }

      this.$swal(obj).then(async (result) => {
        if (result.value) {
          let { device } = panel;
          let params = {
            panel_id: device.id,
            action: panel.active ? "disarm" : "arm",
            mode: result.value
          };

          let descriptionToast = panel.active
            ? `${trans["dmp.disarmPanel"]} ${trans["dmp.panel"]}`
            : `${trans["dmp.armPanel"]} ${trans["dmp.panel"]}`;

          let descriptionContent = panel.active
            ? `${trans["dmp.disarming"]} ${trans["dmp.panel"]} ${panel.name}`
            : `${trans["dmp.arming"]} ${trans["dmp.panel"]} ${panel.name}`;
          loadingToast.value.show(descriptionToast, descriptionContent, "secondary");

          loading.value = true;

          await store
            .dispatch("dmp_panels/armPanel", params)
            .then(() => {
              const messageToast = panel.active
                ? `${trans["dmp.panel"]} ${panel.name} ${trans["dmp.disarmedCorrectly"]}`
                : `${trans["dmp.panel"]} ${panel.name} ${trans["dmp.armedCorrectly"]}`;

              toast({
                component: ToastificationContent,
                props: {
                  title: messageToast,
                  icon: "CheckIcon",
                  variant: "success",
                },
              });

              refetchData();
            })
            .catch((error) => {
              const errorMessage = error.response.data?.message;
              toast({
                component: ToastificationContent,
                props: {
                  title: errorMessage,
                  icon: "AlertTriangleIcon",
                  variant: "danger",
                },
              });
            })
            .finally(() => {
              loadingToast.value.close();
              loading.value = false;
            });
        }
      });
    };

    const syncActions = async (id) => {
      await store
        .dispatch("dmp_panels/syncActions", id)
        .then(() => {
          toast({
            component: ToastificationContent,
            props: {
              title: `${trans["dmp.actions"]} ${trans["dmp.syncCorrectly"]}`,
              icon: "CheckIcon",
              variant: "success",
            },
          });
        })
        .catch((error) => {
          const errorMessage = error.response.data?.message;

          toast({
            component: ToastificationContent,
            props: {
              title: errorMessage,
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    };

    const syncEvents = async (id) => {
      await store
        .dispatch("dmp_panels/syncEvents", id)
        .then(() => {
          toast({
            component: ToastificationContent,
            props: {
              title: `${trans["dmp.events"]} ${trans["dmp.syncCorrectly"]}`,
              icon: "CheckIcon",
              variant: "success",
            },
          });
        })
        .catch((error) => {
          const errorMessage = error.response.data?.message;

          toast({
            component: ToastificationContent,
            props: {
              title: errorMessage,
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    };

    const syncStatus = async (id) => {
      await store
        .dispatch("dmp_panels/syncStatus", id)
        .then(() => {
          toast({
            component: ToastificationContent,
            props: {
              title: `${trans["dmp.status"]} ${trans["dmp.syncCorrectly"]}`,
              icon: "CheckIcon",
              variant: "success",
            },
          });
        })
        .catch((error) => {
          const errorMessage = error.response.data?.message;
          toast({
            component: ToastificationContent,
            props: {
              title: errorMessage,
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    };

    const generateTemplate = async (panel) => {
      const result = await Swal.fire({
        title: proxy.$t("dmp.templateConfig.generateTemplate"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: proxy.$t("dmp.templateConfig.generateTemplate"),
        cancelButtonText: proxy.$t("actions.cancel"),
        customClass: {
          confirmButton: "btn btn-primary text-capitalize",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        html: `
          <p>${proxy.$t("dmp.templateConfig.generateTemplateWarning")}</p>
          <input id="swal-name" class="swal2-input" placeholder="Name" value="${panel.name}">
          <textarea id="swal-description" class="swal2-textarea" placeholder="Description"></textarea>
        `,
        preConfirm: () => {
          const name = document.getElementById("swal-name").value;
          const description = document.getElementById("swal-description").value;

          if (!name) {
            Swal.showValidationMessage("Name is required");
            return false;
          }

          return { name, description };
        },
        buttonsStyling: false,
      });

      if (!result.isConfirmed) return;

      let descriptionToast = proxy.$t("dmp.templateConfig.generateTemplate");
      let descriptionContent = proxy.$t("dmp.templateConfig.generatingTemplate");



      loadingToast.value.show(descriptionToast, descriptionContent, "secondary");

      const payload = {
        id_panel: panel.id,
        name: result.value.name,
        description: result.value.description,
      };

      await store
        .dispatch("dmp_templates/generateTemplate", payload)
        .then(() => {
          loadingToast.value.close();
          refetchData();

          let confirmationTitle = proxy.$t("dmp.templateConfig.generateTemplate");
          let confirmationContent = proxy.$t("dmp.templateConfig.generatedCorrectly");

          toast({
            component: ToastificationContent,
            props: {
              title: `${confirmationTitle} ${confirmationContent}`,
              icon: "CheckIcon",
              variant: "success",
            },
          });
        })
        .catch((error) => {
          loadingToast.value.close();
          const errorMessage = error.response.data?.message || error.message;
          toast({
            component: ToastificationContent,
            props: {
              title: errorMessage,
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    };

    const confirmApplyTemplate = async (panel) => {
      selectedPanel.value = panel;

      await store
        .dispatch("dmp_templates/getAll", {})
        .then((response) => {
          templateOptions.value = response.data.map(template => ({
            value: template.id,
            label: template.template_name,
            description: template.description,
          }));


          isApplyTemplateSidebarActive.value = true;
        })
        .catch((error) => {
          toast({
            component: ToastificationContent,
            props: {
              title: error.response?.data?.message || "Error loading templates",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    };

    const rebuild = async (panel) => {
      let params = {
        panel_id: panel.uuid,
      };

      await store
        .dispatch("dmp_panels/rebuild", params)
        .then((response) => {
          toast({
            component: ToastificationContent,
            props: {
              title: `${response.message}`,
              icon: "CheckIcon",
              variant: "success",
            },
          });

          refetchData();
        })
        .catch((error) => {
          const errorMessage = error.response.data?.message;

          toast({
            component: ToastificationContent,
            props: {
              title: errorMessage,
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    };

    const restart = async (panel) => {
      let params = {
        panel_id: panel.uuid,
      };

      await store
        .dispatch("dmp_panels/restart", params)
        .then((response) => {
          toast({
            component: ToastificationContent,
            props: {
              title: `${response.message}`,
              icon: "CheckIcon",
              variant: "success",
            },
          });

          refetchData();
        })
        .catch((error) => {
          const errorMessage = error.response.data?.message;

          toast({
            component: ToastificationContent,
            props: {
              title: errorMessage,
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    };

    const reconnect = async (panel) => {
      let params = {
        panel_id: panel.uuid,
        data: {},
      };

      await store
        .dispatch("dmp_panels/reconnect", params)
        .then((response) => {
          toast({
            component: ToastificationContent,
            props: {
              title: `${response.message}`,
              icon: "CheckIcon",
              variant: "success",
            },
          });

          refetchData();
        })
        .catch((error) => {
          const errorMessage = error.response.data?.message;

          toast({
            component: ToastificationContent,
            props: {
              title: errorMessage,
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    };

    const onCopy = () => {
      toast({
        component: ToastificationContent,
        props: {
          title: trans["messages.copy.copied_successfully"],
          icon: "ClipboardIcon",
          variant: "success",
        },
      });
    };

    const onTemplateApplied = ({ panelId, templateId, templateName }) => {
      const userId = store.state.user.user.id

      const exists = activeProcesses.value.find(p => p.panelId === panelId && p.templateId === templateId)
      if (exists) return

      busyPanels.value = [...busyPanels.value, panelId]
      activeProcesses.value.push({ userId, panelId, templateId, templateName })
    }

    const onTemplateDone = ({ panelId }) => {
      busyPanels.value = busyPanels.value.filter(id => id !== panelId)
      activeProcesses.value = activeProcesses.value.filter(p => p.panelId !== panelId)
      refetchData()
    }

    return {
      isApplyTemplateSidebarActive,
      selectedPanel,
      templateOptions,
      confirmApplyTemplate,
      nameNav,
      addItemFilter,
      itemFilter,
      addItemFilterSearch,
      confirmApplyTemplate,
      isWizardActive,
      closeWizard,
      isAdd,
      itemEdit,
      openEdit,
      openAdd,
      confirmDelete,
      update,
      fetchList,
      tableColumns,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      refetchData,
      formatDateAssigned,

      centerFilter,
      sectorFilter,
      facilitiesOptions,
      centerOptions,
      sectorOptions,
      resellerOptions,
      facilitiesFilter,
      profileFilter,
      profileOptions,

      copyToClipboard,
      onCopy,
      onError,

      filtersCount,
      buildFilters,
      clearSearchFilters,
      setFacetedFilters,
      unitsSite,
      loadingToast,
      loading,

      armPanel,
      syncEvents,
      syncActions,
      syncStatus,
      generateTemplate,
      restart,
      rebuild,
      reconnect,
      onTemplateApplied,
      onTemplateDone,
      activeProcesses,
      exportExcel,
      buttonExport,
      searchOptions,
      customQueryParams,
      municipalityOptions,
      regionOptions,
    };
  },
};
</script>

<style scoped>
.device-list-actions {
  flex-wrap: wrap;
  gap: 0.5rem;
}

.device-list-search {
  flex: 1 1 320px;
  width: auto;
  min-width: 260px;
}

.device-list-actions .device-list-search,
.device-list-actions .btn {
  margin-right: 0 !important;
  margin-left: 0 !important;
}

.btn-orange {
  background-color: orange;
  color: white;
}

.btn-orange:hover {
  box-shadow: 0 8px 25px -8px orange;
}
</style>
